import { useState, useEffect, useCallback } from 'react';
import declaracionRentaService from '../services/declaracionRentaService';

/**
 * Hook personalizado para manejar la lógica de Declaración de Renta
 * Diseñado para ser usado por agentes autónomos vía MCP
 *
 * @param {number} year - Año de la declaración
 * @param {number} id_company - ID de la empresa
 * @param {string} token - Token de autenticación
 * @returns {Object} Estado y funciones para manejar declaraciones
 */
const useDeclaracionRenta = (year, id_company, token) => {
    const [declaracionesJuradas, setDeclaracionesJuradas] = useState([]);
    const [declaracionesRenta, setDeclaracionesRenta] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Cargar declaraciones juradas para el año seleccionado
     */
    const fetchDeclaracionesJuradas = useCallback(async () => {
        if (!year || !id_company) return;

        setLoading(true);
        setError(null);

        try {
            const data = await declaracionRentaService.getDeclaracionesJuradas(
                year,
                id_company,
                token
            );
            setDeclaracionesJuradas(data);
        } catch (err) {
            setError(err.message || 'Error al cargar declaraciones juradas');
            console.error('Error fetching declaraciones juradas:', err);
        } finally {
            setLoading(false);
        }
    }, [year, id_company, token]);

    /**
     * Cargar declaraciones de renta para el año seleccionado
     */
    const fetchDeclaracionesRenta = useCallback(async () => {
        if (!year || !id_company) return;

        setLoading(true);
        setError(null);

        try {
            const data = await declaracionRentaService.getDeclaracionesRenta(
                year,
                id_company,
                token
            );
            setDeclaracionesRenta(data);
        } catch (err) {
            setError(err.message || 'Error al cargar declaraciones de renta');
            console.error('Error fetching declaraciones renta:', err);
        } finally {
            setLoading(false);
        }
    }, [year, id_company, token]);

    /**
     * Crear nueva declaración jurada
     */
    const createDeclaracionJurada = useCallback(async (formData) => {
        setLoading(true);
        setError(null);

        try {
            const result = await declaracionRentaService.createDeclaracionJurada(
                { ...formData, year, id_company },
                token
            );
            await fetchDeclaracionesJuradas(); // Recargar lista
            return result;
        } catch (err) {
            setError(err.message || 'Error al crear declaración jurada');
            console.error('Error creating declaracion jurada:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [year, id_company, token, fetchDeclaracionesJuradas]);

    /**
     * Crear nueva declaración de renta
     */
    const createDeclaracionRenta = useCallback(async (formData) => {
        setLoading(true);
        setError(null);

        try {
            const result = await declaracionRentaService.createDeclaracionRenta(
                { ...formData, year, id_company },
                token
            );
            await fetchDeclaracionesRenta(); // Recargar lista
            return result;
        } catch (err) {
            setError(err.message || 'Error al crear declaración de renta');
            console.error('Error creating declaracion renta:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [year, id_company, token, fetchDeclaracionesRenta]);

    /**
     * Actualizar declaración jurada existente
     */
    const updateDeclaracionJurada = useCallback(async (id, formData) => {
        setLoading(true);
        setError(null);

        try {
            const result = await declaracionRentaService.updateDeclaracionJurada(
                id,
                { ...formData, id_company },
                token
            );
            await fetchDeclaracionesJuradas(); // Recargar lista
            return result;
        } catch (err) {
            setError(err.message || 'Error al actualizar declaración jurada');
            console.error('Error updating declaracion jurada:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [id_company, token, fetchDeclaracionesJuradas]);

    /**
     * Eliminar declaración jurada
     */
    const deleteDeclaracionJurada = useCallback(async (id) => {
        setLoading(true);
        setError(null);

        try {
            await declaracionRentaService.deleteDeclaracionJurada(id, id_company, token);
            await fetchDeclaracionesJuradas(); // Recargar lista
        } catch (err) {
            setError(err.message || 'Error al eliminar declaración jurada');
            console.error('Error deleting declaracion jurada:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [id_company, token, fetchDeclaracionesJuradas]);

    // Cargar datos cuando cambia el año
    useEffect(() => {
        if (year && id_company && token) {
            fetchDeclaracionesJuradas();
            fetchDeclaracionesRenta();
        }
    }, [year, id_company, token, fetchDeclaracionesJuradas, fetchDeclaracionesRenta]);

    return {
        // Estado
        declaracionesJuradas,
        declaracionesRenta,
        loading,
        error,

        // Funciones
        fetchDeclaracionesJuradas,
        fetchDeclaracionesRenta,
        createDeclaracionJurada,
        createDeclaracionRenta,
        updateDeclaracionJurada,
        deleteDeclaracionJurada,

        // Utilidades
        refresh: () => {
            fetchDeclaracionesJuradas();
            fetchDeclaracionesRenta();
        }
    };
};

export default useDeclaracionRenta;
